<template>
  <div class="identity-backdrop">
    <div class="identity-card linked-logins">
      <!-- The same back arrow the phone pages carry. Signing out this device is
           the App Menu's; Active sessions below signs out the others, or all. -->
      <button class="linked-logins__back" type="button" :aria-label="t('authentication.linking.backToAppButton')" @click="goBack">
        <i class="pi pi-chevron-left" />
      </button>

      <div class="identity-card__brand">
        <div class="auto-auction-logo" aria-label="AutoAuction.com">
          <span class="auto-auction-logo__auto">Auto</span><span class="auto-auction-logo__auction">Auction</span><span class="auto-auction-logo__top-level-domain">.com</span>
        </div>
      </div>

      <header class="linked-logins__head">
        <h1 class="identity-card__title">{{ t('authentication.linking.pageTitle') }}</h1>
        <p v-if="session.user" class="linked-logins__who">
          {{ t('authentication.linking.signedInAs') }}
          <strong class="linked-logins__name">{{ session.user.name }}</strong>
          <span v-if="session.user.primaryEmail" class="linked-logins__email">{{ session.user.primaryEmail }}</span>
        </p>
      </header>

      <template v-if="view === 'closing'">
        <!-- Close my account: the explanation, then one final confirm
             (UI ruling 5 — no forced re-login; step-up is a future, global rule). -->
        <section class="section">
          <h2 class="section__title section__title--danger">{{ t('authentication.linking.closeTitle') }}</h2>
          <p class="lede">{{ t('authentication.linking.closeLede') }}</p>
          <button class="button button--danger" type="button" :disabled="busy" @click="askClose">
            {{ t('authentication.linking.closeConfirmButton') }}
          </button>
          <button class="button button--ghost" type="button" @click="view = 'list'">
            {{ t('authentication.linking.closeCancelButton') }}
          </button>
        </section>
      </template>

      <template v-else>
        <section class="section">
          <h2 class="section__title">{{ t('authentication.linking.connectionsTitle') }}</h2>
          <ul class="connections">
            <li v-for="connection in linking.connections" :key="`${connection.provider}:${connection.subject}`" class="connection">
              <span class="connection__icon"><i :class="`pi ${providerIcon(connection.provider)}`" /></span>
              <div class="connection__body">
                <div class="connection__label">{{ providerLabel(connection.provider) }}</div>
                <div v-if="connection.email" class="connection__sub">{{ connection.email }}</div>
                <div v-if="connection.connectedAt" class="connection__sub">
                  {{ t('authentication.linking.connectedOn', { date: shortDate(connection.connectedAt) }) }}
                </div>
              </div>
              <button
                class="connection__unlink"
                type="button"
                :disabled="!linking.canUnlink || busy"
                :title="!linking.canUnlink ? t('authentication.linking.unlinkLastHint') : undefined"
                @click="askUnlink(connection)"
              >
                {{ t('authentication.linking.unlinkButton') }}
              </button>
            </li>
          </ul>
          <p v-if="!linking.canUnlink && linking.connections.length === 1" class="hint">
            {{ t('authentication.linking.unlinkLastHint') }}
          </p>

          <template v-if="view === 'picking'">
            <h3 class="section__subtitle">{{ t('authentication.linking.addLoginTitle') }}</h3>
            <p class="hint">{{ t('authentication.linking.addLoginHint') }}</p>
            <div class="picker">
              <button
                v-for="provider in PROVIDERS"
                :key="provider.label"
                class="button button--ghost"
                type="button"
                :disabled="busy"
                @click="addLogin(provider)"
              >
                <i :class="`pi ${provider.icon}`" /> {{ provider.label }}
              </button>
            </div>
          </template>
          <button v-else class="button button--primary" type="button" :disabled="busy" @click="view = 'picking'">
            {{ t('authentication.linking.addLoginButton') }}
          </button>
        </section>

        <!-- Two-step sign-in: the authenticator app, turned on here by a person who is
             already signed in. Only an email-and-password sign-in has anything to
             manage; a Google or Microsoft sign-in is covered by the provider's own. -->
        <section class="section" data-section="second-factor">
          <h2 class="section__title">{{ t('authentication.mfa.title') }}</h2>
          <p class="hint">{{ t('authentication.mfa.lede') }}</p>
          <p v-if="loaded && !available" class="hint" data-mfa="not-available">{{ t('authentication.mfa.notAvailable') }}</p>

          <template v-if="loaded && available">
            <div class="mfa__status">
              <span class="mfa__state" :data-mfa-state="enabled ? 'on' : 'off'">
                {{ enabled ? t('authentication.mfa.statusOn') : t('authentication.mfa.statusOff') }}
              </span>
              <button v-if="enabled" class="connection__unlink" type="button" :disabled="busy || working" @click="askTurnOff">
                {{ t('authentication.mfa.turnOffButton') }}
              </button>
              <button v-else-if="!isSettingUp" class="button button--ghost" type="button" :disabled="busy || working" @click="startSetup">
                {{ t('authentication.mfa.turnOnButton') }}
              </button>
            </div>

            <div v-if="isSettingUp" class="mfa__setup" data-mfa="setup">
              <h3 class="section__subtitle">{{ t('authentication.mfa.setupTitle') }}</h3>
              <p class="hint">{{ t('authentication.mfa.scanStep') }}</p>
              <img v-if="qrImage" class="mfa__qr" :src="qrImage" :alt="t('authentication.mfa.scanAlt')" width="192" height="192" />
              <p class="hint">{{ t('authentication.mfa.enterByHand') }}</p>
              <code class="mfa__secret" data-mfa="secret">{{ setup.secret }}</code>
              <label class="mfa__field">
                <span class="hint">{{ t('authentication.mfa.codeLabel') }}</span>
                <input
                  v-model="code"
                  class="mfa__input"
                  data-mfa="code"
                  type="text"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  maxlength="6"
                  pattern="[0-9]{6}"
                  @keydown.enter.prevent="finishSetup"
                />
              </label>
              <p class="hint">{{ t('authentication.mfa.lostWarning') }}</p>
              <div class="mfa__actions">
                <button class="button" type="button" data-mfa="confirm" :disabled="busy || working || code.trim().length !== 6" @click="finishSetup">
                  {{ t('authentication.mfa.confirmButton') }}
                </button>
                <button class="button button--ghost" type="button" data-mfa="cancel" :disabled="working" @click="cancelSetup">
                  {{ t('authentication.mfa.cancelButton') }}
                </button>
              </div>
            </div>
          </template>

          <p v-if="message" class="hint mfa__message" role="alert" data-mfa="message">{{ message }}</p>
        </section>

        <!-- Active sessions: one row per device, this one first. Each other
             device signs out on its own; signing out everywhere ends them all,
             this one included. -->
        <section class="section" data-section="active-sessions">
          <h2 class="section__title">{{ t('authentication.sessions.title') }}</h2>
          <p class="hint">{{ t('authentication.sessions.lede') }}</p>
          <p v-if="sessionsLoadFailed" class="hint">{{ t('authentication.sessions.loadFailed') }}</p>
          <ul class="session-list">
            <li v-for="row in sessionRows" :key="row.id" class="session" :data-session="row.id">
              <span class="connection__icon"><i :class="`pi ${row.icon}`" /></span>
              <div class="connection__body">
                <div class="connection__label">
                  {{ row.appName }}
                  <span v-if="row.isThisDevice" class="session__this-device">{{ t('authentication.sessions.thisDevice') }}</span>
                </div>
                <div class="connection__sub">{{ row.device }}</div>
                <div v-for="detail in row.details" :key="detail" class="connection__sub">{{ detail }}</div>
              </div>
              <button
                v-if="!row.isThisDevice"
                class="connection__unlink"
                type="button"
                :disabled="busy"
                @click="askSignOutDevice(row)"
              >
                {{ t('authentication.sessions.signOutButton') }}
              </button>
            </li>
          </ul>
          <button class="button button--danger" type="button" :disabled="busy" @click="askSignOutEverywhere">
            <i class="pi pi-sign-out" /> {{ t('authentication.sessions.everywhereButton') }}
          </button>
        </section>

        <section class="section section--foot">
          <button class="close-row" type="button" @click="view = 'closing'">
            <i class="pi pi-times-circle" /> {{ t('authentication.linking.closeRow') }}
          </button>
        </section>
      </template>

      <!-- Unlink and close can fail: the drawer stays open through the attempt
           and a failure answers inside it, with confirming again as the retry —
           never a toast (identity effort ruling 2026-09-21). It anchors to the
           card and dims only the card. -->
      <ConfirmDrawer
        v-model:open="confirm.open"
        :title="confirm.title"
        :message="confirm.message"
        :confirm-label="confirm.confirmLabel"
        :cancel-label="confirm.cancelLabel"
        :destructive="confirm.destructive"
        :close-on-confirm="false"
        :busy="busy"
        :error="confirm.error"
        @confirm="onConfirm"
      />
    </div>
  </div>
</template>

<script setup>
import '../shared/identity-card.css';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ConfirmDrawer } from '@hds/auto-auction-com-components-desktop';
import {
  IDENTITY_PATHS,
  brokeredLogin,
  identityClient,
  linkingStore,
  sessionStore,
  sessionTokenStorage,
  startLink
} from '@hds/identity-client-sdk';

import { useActiveSessions } from '../shared/activeSessions.js';
import { useSecondFactor } from '../shared/secondFactor.js';
import { useBackOrHome } from '../shared/backOrHome.js';

defineOptions({ name: 'LinkedLoginsPage' });

// Sign-in & security (account-linking design, UI rulings 2/3/5): the Human's
// linked logins, the "Add a login" picker, and Close my account. Unlink is
// disabled on the last connection (item 6) — the server refuses it regardless.
// Same brain as the mobile screen, in the desktop identity card; the confirms
// are a ConfirmDrawer over the card rather than a sheet over the phone.

const { t } = useI18n();
const session = sessionStore();
const linking = linkingStore();

const view = ref('list'); // list | picking | closing
const busy = ref(false);
// The in-card confirm drawer's state, set by the `ask*` handlers; `error` is
// the failed attempt's verdict, shown inside the open drawer.
const confirm = ref({ open: false, title: '', message: '', confirmLabel: '', cancelLabel: '', destructive: false, error: '', action: null });

// The pool's provider names (identity_provider values); null = the local
// directory's own email/password form.
const PROVIDERS = [
  { pool: 'Google', label: 'Google', icon: 'pi-google' },
  { pool: 'Microsoft', label: 'Microsoft', icon: 'pi-microsoft' },
  { pool: null, label: t('authentication.linking.providerLocal'), icon: 'pi-envelope' }
];

const PROVIDER_META = {
  google: { label: 'Google', icon: 'pi-google' },
  microsoft: { label: 'Microsoft', icon: 'pi-microsoft' },
  local: { label: t('authentication.linking.providerLocal'), icon: 'pi-envelope' }
};

function providerLabel(provider) {
  return PROVIDER_META[provider]?.label ?? provider;
}

function providerIcon(provider) {
  return PROVIDER_META[provider]?.icon ?? 'pi-user';
}

// Only called for a login that carries a date. An unreadable date reads as
// "Invalid Date" rather than throwing, so there is nothing to catch.
function shortDate(iso) {
  return new Date(iso).toLocaleDateString();
}

// Back to where the person came from, or to the app's front door when there is
// nothing to pop — this page is often reached by a router.replace from the
// link-outcome acknowledgement.
const goBack = useBackOrHome();

const { rows: sessionRows, loadFailed: sessionsLoadFailed, askSignOutDevice, askSignOutEverywhere } = useActiveSessions({ confirm, busy });
const { available, enabled, setup, working, loaded, isSettingUp, message, code, qrImage, startSetup, finishSetup, cancelSetup, askTurnOff } = useSecondFactor({ confirm, busy });

onMounted(() => linking.refresh());

async function addLogin(provider) {
  busy.value = true;
  // Terminal: link-mode hosted login; the return trip lands on the link-outcome
  // acknowledgement page, whose one button leads back here.
  await startLink(provider.pool ?? undefined, IDENTITY_PATHS.signInSecurity);
}

// Destructive confirms run through the in-card ConfirmDrawer (never the
// browser's native confirm popup): each `ask*` opens the drawer with its copy
// and stashes the action to run on confirm.
function askUnlink(connection) {
  confirm.value = {
    open: true,
    title: t('authentication.linking.unlinkButton'),
    message: t('authentication.linking.unlinkConfirm'),
    confirmLabel: t('authentication.linking.unlinkButton'),
    cancelLabel: t('authentication.linking.sheetCancelButton'),
    destructive: false,
    error: '',
    action: () => doUnlink(connection)
  };
}

async function doUnlink(connection) {
  busy.value = true;
  confirm.value.error = '';
  try {
    await linking.unlink(connection.provider, connection.subject);
    confirm.value.open = false;
  } catch (e) {
    // The drawer stays open with the verdict; confirming again retries.
    confirm.value.error = e?.body?.error || t('authentication.linking.errorGeneric');
  } finally {
    busy.value = false;
  }
}

function askClose() {
  confirm.value = {
    open: true,
    title: t('authentication.linking.closeTitle'),
    message: t('authentication.linking.closeFinalConfirm'),
    confirmLabel: t('authentication.linking.closeConfirmButton'),
    cancelLabel: t('authentication.linking.closeCancelButton'),
    destructive: true,
    error: '',
    action: doClose
  };
}

async function doClose() {
  busy.value = true;
  confirm.value.error = '';
  try {
    await identityClient.closeAccount();
    await sessionTokenStorage.clearSession();
    // A session-less hydrate clears the user AND the boot-paint cache; then end
    // at the hosted logout so the provider session dies with the account's.
    await session.hydrate();
    brokeredLogin.beginLogout();
  } catch {
    // The drawer stays open with the verdict; confirming again retries.
    confirm.value.error = t('authentication.linking.errorGeneric');
    busy.value = false;
  }
}

function onConfirm() {
  const run = confirm.value.action;
  if (run) run();
}
</script>

<style scoped>
/* The shared card skin lives in ../shared/identity-card.css; the list, the
   picker and the foot are this page's own. */

.linked-logins__head {
  margin-bottom: 18px;
}

.linked-logins__who {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: var(--p-text-muted-color);
}

.linked-logins__name {
  color: var(--p-text-color);
  margin-left: 4px;
}

.linked-logins__email {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section + .section {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid var(--p-content-border-color);
}

.section__title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--p-text-color);
}

.section__title--danger {
  color: var(--p-tag-danger-background);
}

.section__subtitle {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.lede {
  margin: 0;
  color: var(--p-text-muted-color);
  font-size: 14px;
}

.hint {
  margin: 0;
  color: var(--p-text-muted-color);
  font-size: 13px;
}

.connections,
.session-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.connection,
.session {
  display: flex;
  align-items: center;
  gap: 12px;
}

.connection__icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-ground);
  color: var(--p-primary-color);
}

.connection__body {
  flex: 1;
  min-width: 0;
}

.connection__label {
  font-size: 15px;
}

.connection__sub {
  font-size: 13px;
  color: var(--p-text-muted-color);
  overflow: hidden;
  text-overflow: ellipsis;
}

.connection__unlink {
  padding: 8px 12px;
  border: 1px solid var(--p-content-border-color);
  border-radius: 8px;
  background: transparent;
  color: var(--p-text-color);
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
}

.connection__unlink:disabled {
  opacity: 0.5;
  cursor: default;
}

.picker {
  display: flex;
  flex-direction: column;
}

.picker .button--ghost {
  margin-bottom: 8px;
}

.identity-card .button--danger {
  height: 48px;
  background: var(--p-tag-danger-background);
  color: var(--p-tag-danger-color);
  font-size: 15px;
  margin-bottom: 10px;
}

.section--foot {
  gap: 4px;
}

.session__this-device {
  margin-left: 6px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--p-primary-color);
  color: var(--p-primary-contrast-color);
  font-size: 11px;
  font-weight: 600;
  vertical-align: middle;
}

.mfa__status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.mfa__state {
  font-size: 15px;
  font-weight: 600;
}

.mfa__state[data-mfa-state='on'] {
  color: var(--p-primary-color);
}

.mfa__setup {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mfa__qr {
  align-self: center;
  background: #fff;
  padding: 8px;
  border-radius: var(--p-radius-row);
}

.mfa__secret {
  align-self: center;
  max-width: 100%;
  overflow-wrap: anywhere;
  text-align: center;
  font-size: 14px;
  letter-spacing: 0.08em;
}

.mfa__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mfa__input {
  padding: 12px;
  border: 1px solid var(--p-content-border-color, var(--p-text-muted-color));
  border-radius: var(--p-radius-row);
  background: var(--surface-ground);
  color: var(--p-text-color);
  font-size: 18px;
  letter-spacing: 0.3em;
  text-align: center;
}

.mfa__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.mfa__message {
  color: var(--p-tag-danger-background);
}

.close-row {
  border: none;
  background: none;
  font-family: inherit;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 6px 0;
}

.close-row {
  color: var(--p-tag-danger-background);
}

/* The phone pages' back arrow, drawn from the same tokens their BackButton
   reads in its filled variant, so it is the same control here. */
.linked-logins__back {
  align-self: flex-start;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: var(--p-back-button-filled-border-radius);
  background: var(--p-back-button-filled-background);
  color: var(--p-back-button-filled-color);
  display: grid;
  place-items: center;
  cursor: pointer;
}

.linked-logins__back i {
  font-size: var(--p-back-button-filled-icon-font-size);
}

.linked-logins__back:hover {
  text-decoration: underline;
}
</style>
